import { ElMessage } from 'element-plus'
import html2canvas, { type Options } from 'html2canvas'
import { i18n } from 'i18n-utils'
import { getUserCenterPageRoute, UserCenterRoutes } from 'platform-config/src/router/PageRoutes'
import { pinia, useUserInfoStore } from 'store'
import { type App, type Component } from 'vue'
import type { Router } from 'vue-router'
import { ClosePosition, Popup, UpdateMargin, type ClosePositionProps, type PopupProps, type UpdateMarginProps } from '..'
import { AssetTransfer, type AssetTransferProps } from '../business-components/AssetTransfer'
import { ClosePositionDetail, type ClosePositionDetailProps } from '../business-components/ClosePositionDetail'
import { EntrustDetail, type EntrustDetailProps } from '../business-components/EntrustDetail'
import { ExchangeConfirm, type ExchangeConfirmProps } from '../business-components/ExchangeConfirm'
import { FinancePositionDetail, type FEFinancePositionDetailProps } from '../business-components/FinancePositionDetail'
import { OpenPositionDetail, type OpenPositionDetailProps } from '../business-components/OpenPositionDetail'
import { StopProfitStopLoss, type StopProfitStopLossProps } from '../business-components/StopProfitStopLoss'
import { StrategyDetail, type StrategyDetailProps } from '../business-components/StrategyDetail'
import { SubscriptionFinancial, type SubscriptionFinancialProps } from '../business-components/SubscriptionFinancial'
import { TradePasswordModal, type TradePasswordModalProps } from '../business-components/TradePasswordModal'
import { ConfirmPassword, ConfirmPasswordTypeMap, type ConfirmPasswordProps, type ConfirmPasswordType, type DefaultConfirmPasswordResult } from '../components/ConfirmPassword'
import { KycVerifyDialog } from '../components/KycVerifyDialog'
import { SecurityAuth, type SecurityAuthProps } from '../components/SecurityAuth'
import { CustomDialog } from './CustomDialog'

export const copyToClipboard = (text: string | number) => {
  navigator.clipboard.writeText(text.toString())
  UiUtils.showSuccess(i18n.global.t('复制成功'))
}

interface CommonDialogDrawerProps {
  onClose?: () => void
  onCancel?: () => void
  onConfirm?: ((done: () => void) => Promise<void> | void) | (() => Promise<void> | void)
}

export class UiUtils {
  static errorMessageQueue: string[] = []
  static infoMessageQueue: string[] = []
  static warningMessageQueue: string[] = []
  static successMessageQueue: string[] = []
  static app: App<Element> | null = null
  static initMessageZIndex = 9999
  static gotoLogin?: () => void
  static router?: Router
  static gotoRecharge?: () => void
  static gotoSecuritySettings?: () => void
  static closeKycDialog?: () => void
  static showSuccess(message: string) {
    if (UiUtils.successMessageQueue.indexOf(message) !== -1) {
      return
    }
    UiUtils.successMessageQueue.push(message)
    ElMessage.success({
      message,
      duration: 3000,
      zIndex: ++UiUtils.initMessageZIndex,
      onClose: () => {
        UiUtils.successMessageQueue = UiUtils.successMessageQueue.filter((item) => item !== message)
      },
    })
  }

  static showError(message: string) {
    if (UiUtils.errorMessageQueue.indexOf(message) !== -1) {
      return
    }
    UiUtils.errorMessageQueue.push(message)

    ElMessage.error({
      message,
      duration: 3000,
      zIndex: ++UiUtils.initMessageZIndex,
      onClose: () => {
        UiUtils.errorMessageQueue = UiUtils.errorMessageQueue.filter((item) => item !== message)
      },
    })
  }

  static showWarning(message: string) {
    if (UiUtils.warningMessageQueue.indexOf(message) !== -1) {
      return
    }
    UiUtils.warningMessageQueue.push(message)
    ElMessage.warning({
      message,
      duration: 3000,
      zIndex: ++UiUtils.initMessageZIndex,
      onClose: () => {
        UiUtils.warningMessageQueue = UiUtils.warningMessageQueue.filter((item) => item !== message)
      },
    })
  }

  static showInfo(message: string) {
    if (UiUtils.infoMessageQueue.indexOf(message) !== -1) {
      return
    }
    UiUtils.infoMessageQueue.push(message)
    ElMessage.info({
      message,
      duration: 3000,
      zIndex: ++UiUtils.initMessageZIndex,
      onClose: () => {
        UiUtils.infoMessageQueue = UiUtils.infoMessageQueue.filter((item) => item !== message)
      },
    })
  }

  static showModal(
    message: string | Component,
    dialogProps?: Partial<import('element-plus').DialogProps> & {
      onClose?: () => void
      confirmButtonText?: string
      cancelButtonText?: string
      showConfirm?: boolean
      onConfirm?: ((done: () => void) => Promise<void> | void) | (() => Promise<void> | void)
      placement?: 'center' | 'bottom' | 'right'
      onCancel?: () => void
    },
  ) {
    const el = document.createElement('div')
    const close = () => {
      setTimeout(() => {
        app.unmount()
        document.body.removeChild(el)
      }, 300)
    }
    const app = this.createDialogApp(CustomDialog, {
      ...dialogProps,
      title: dialogProps?.title || i18n.global.t('温馨提示'),
      modelValue: true,
      width: dialogProps?.width || 360,
      content: message,
      onCancel: () => {
        dialogProps?.onCancel?.()
        close()
      },
    })
    this.mountApp(app, el)
    return {
      close: close,
    }
  }

  static mountApp(app: App<Element>, el: HTMLDivElement) {
    if (this.app) {
      app._context = this.app._context
    }
    app.use(i18n)
    if (this.router) {
      app.use(this.router)
    }
    app.use(pinia)
    app.mount(el)
    document.body.appendChild(el)
  }

  static unmountApp(app: App<Element>, el: HTMLDivElement | null, onClose?: () => void) {
    onClose?.()
    if (!el) return
    const isHasEl = document.body.contains(el)
    if (!isHasEl) return
    setTimeout(() => {
      app.unmount()
      document.body.removeChild(el!)
      el = null
    }, 250)
  }

  static dialogAppPluginProps<T>(app: () => App<Element>, el: HTMLDivElement, props: T & CommonDialogDrawerProps) {
    const mergeProps: Partial<FEFinancePositionDetailProps> = {
      ...props,
      visible: true,
      onClose: () => {
        this.unmountApp(app(), el, props.onClose)
      },
    }
    return mergeProps
  }

  static createDialogApp(component: Component, props: CommonDialogDrawerProps & Record<string, any>) {
    if (!this.app) throw new Error('UiUtils.app is not initialized')
    return createApp(component, props)
  }

  static async showConfirmPassword<T = DefaultConfirmPasswordResult, P extends ConfirmPasswordType = ConfirmPasswordTypeMap.TradePwd>(
    props: Omit<ConfirmPasswordProps<T, P>, 'modelValue' | 'onClose' | 'onReject' | 'onResolve'> & {
      onResolve?: ConfirmPasswordProps<T, P>['onResolve']
      onClose?: ConfirmPasswordProps<T, P>['onClose']
    },
  ) {
    const el = document.createElement('div')
    const userInfoStore = useUserInfoStore()
    if (!userInfoStore.isSetTradePassword) {
      const res = await UiUtils.openTradePasswordModal({
        onMounted: () => {
          UiUtils.showWarning(i18n.global.t('请先设置支付密码'))
        },
      })
      if (!res) {
        return Promise.reject(false)
      }
    }
    return new Promise<T>((resolve, reject) => {
      if (!this.app) throw new Error('UiUtils.app is not initialized')
      const mergeProps: ConfirmPasswordProps = {
        ...props,
        modelValue: true,
        onResolve: (data) => {
          resolve(data as T)
          return props.onResolve?.(data as T)
        },
        onClose: () => {
          this.unmountApp(confirmPasswordApp, el, props.onClose)
        },
        onReject: () => {
          reject(false)
        },
      } as ConfirmPasswordProps

      const confirmPasswordApp = createApp(ConfirmPassword, mergeProps)
      this.mountApp(confirmPasswordApp, el)
    })
  }

  static showSecurityAuth<T = ApiResponse.SafetyAuthResponse | void>(props: Partial<SecurityAuthProps<T>>) {
    const el = document.createElement('div')

    return new Promise<T>((resolve, reject) => {
      if (!this.app) throw new Error('UiUtils.app is not initialized')

      const mergeProps: Partial<SecurityAuthProps<T>> = {
        ...props,
        async onResolve(data) {
          await props.onResolve?.(data)
          return resolve(data as T)
        },
        async onReject() {
          return reject()
        },
        onClose() {
          securityAuthApp.unmount()
          document.body.removeChild(el)
          props.onClose?.()
        },
      }
      const securityAuthApp = createApp(SecurityAuth, mergeProps as Record<string, any>)
      this.mountApp(securityAuthApp, el)
    })
  }

  static openTradePasswordModal(props?: Partial<TradePasswordModalProps>) {
    const el = document.createElement('div')
    const userInfoStore = useUserInfoStore()
    if (!userInfoStore.isOpenSecurity) {
      UiUtils.showModal(i18n.global.t('请先绑定谷歌、手机或者邮箱'), {
        confirmButtonText: i18n.global.t('绑定'),
        onConfirm: () => {
          this.gotoSecuritySettings?.()
        },
      })
      return Promise.reject(false)
    }

    return new Promise((resolve, reject) => {
      if (!this.app) throw new Error('UiUtils.app is not initialized')

      const mergeProps: Partial<TradePasswordModalProps> = {
        ...(props || {}),
        async onResolve() {
          await props?.onResolve?.()
          return resolve(true)
        },
        async onReject() {
          return reject()
        },
        onClose: () => {
          this.unmountApp(tradePasswordApp, el, props?.onClose)
        },
      }

      const tradePasswordApp = createApp(TradePasswordModal, mergeProps as Record<string, any>)
      this.mountApp(tradePasswordApp, el)
    })
  }

  static checkLoginPassword(pwd: string) {
    if (pwd.length < 6) {
      throw this.showError(i18n.global.t('密码长度为6-20位字符'))
    }
    if (pwd.includes(' ')) {
      throw this.showError(i18n.global.t('密码不能包含空格'))
    }
    const digRegExp = /^\d+$/
    if (digRegExp.test(pwd)) {
      throw this.showError(i18n.global.t('密码不能为纯数字'))
    }
    const charRegExp = /^[a-zA-Z]+$/
    if (charRegExp.test(pwd)) {
      throw this.showError(i18n.global.t('密码不能为纯字母'))
    }
  }

  static showPopup(props: Partial<PopupProps>) {
    const el = document.createElement('div')
    const app = this.createDialogApp(
      Popup,
      this.dialogAppPluginProps<Partial<PopupProps>>(() => app, el, props),
    )
    this.mountApp(app, el)
  }

  /**
   * 显示资产划转弹窗
   * @param props
   */
  static showAssetTransfer(props: Partial<AssetTransferProps>) {
    const el = document.createElement('div')
    const app = this.createDialogApp(
      AssetTransfer,
      this.dialogAppPluginProps<Partial<AssetTransferProps>>(() => app, el, {
        ...props,
        onRecharge: () => {
          this.gotoRecharge?.()
        },
      }),
    )
    this.mountApp(app, el)
  }

  /**
   * 显示平仓弹窗
   * @param props
   */
  static showClosePosition(props: Partial<ClosePositionProps>) {
    const el = document.createElement('div')
    const app = this.createDialogApp(
      ClosePosition,
      this.dialogAppPluginProps<Partial<ClosePositionProps>>(() => app, el, {
        ...props,
      }),
    )
    this.mountApp(app, el)
  }

  /**
   * 显示更新保证金弹窗
   * @param props
   */
  static showUpdateMargin(props: Partial<UpdateMarginProps>) {
    const el = document.createElement('div')
    const app = this.createDialogApp(
      UpdateMargin,
      this.dialogAppPluginProps<Partial<UpdateMarginProps>>(() => app, el, {
        ...props,
      }),
    )
    this.mountApp(app, el)
  }

  /**
   * 显示止盈止损弹窗
   * @param props
   */
  static showStopProfitStopLoss(props: Partial<StopProfitStopLossProps>) {
    const el = document.createElement('div')
    const app = this.createDialogApp(
      StopProfitStopLoss,
      this.dialogAppPluginProps<Partial<StopProfitStopLossProps>>(() => app, el, props),
    )
    this.mountApp(app, el)
  }

  /**
   * 显示持仓详情弹窗
   * @param props
   */
  static showPositionDetail(props: Partial<OpenPositionDetailProps>) {
    const el = document.createElement('div')
    const app = this.createDialogApp(
      OpenPositionDetail,
      this.dialogAppPluginProps<Partial<OpenPositionDetailProps>>(() => app, el, props),
    )
    this.mountApp(app, el)
  }

  /** 显示已平仓详情弹窗 */
  static showClosePositionDetail(props: Partial<ClosePositionDetailProps>) {
    const el: HTMLDivElement | null = document.createElement('div')
    const app = this.createDialogApp(
      ClosePositionDetail,
      this.dialogAppPluginProps<Partial<ClosePositionDetailProps>>(() => app, el, props),
    )
    this.mountApp(app, el)
  }

  /** 显示策略详情弹窗 */
  static showStrategyDetail(props: Partial<StrategyDetailProps>) {
    const el = document.createElement('div')
    const app = this.createDialogApp(
      StrategyDetail,
      this.dialogAppPluginProps<Partial<StrategyDetailProps>>(() => app, el, props),
    )
    this.mountApp(app, el)
  }

  /**
   * 显示委托详情弹窗
   * @param props
   */
  static showEntrustDetail(props: Partial<EntrustDetailProps>) {
    const el = document.createElement('div')
    const app = this.createDialogApp(
      EntrustDetail,
      this.dialogAppPluginProps<Partial<EntrustDetailProps>>(() => app, el, props),
    )
    this.mountApp(app, el)
  }

  /**
   * 显示KYC认证弹窗
   */
  static showKycDialog(): Promise<void> {
    const el = document.createElement('div')

    return new Promise((resolve) => {
      if (!this.app) throw new Error('UiUtils.app is not initialized')
      const kycDialogApp = createApp(KycVerifyDialog, {
        onClose: () => {
          kycDialogApp.unmount()
          document.body.removeChild(el)
          resolve()
        },
        onConfirm: () => {
          kycDialogApp.unmount()
          document.body.removeChild(el)
          // 跳转到身份认证页面
          this.router?.push(getUserCenterPageRoute(UserCenterRoutes.RealNameAuth))
          resolve()
        },
      })
      UiUtils.closeKycDialog = () => {
        try {
          kycDialogApp.unmount()
          if (document.body.contains(el)) {
            document.body.removeChild(el)
          }
        } catch (e) {
          console.log(e)
        }
      }
      this.mountApp(kycDialogApp, el)
    })
  }

  /**
   * 显示下单确认弹窗
   * @param props
   */
  static showExchangeConfirm(props: Partial<ExchangeConfirmProps>) {
    const el = document.createElement('div')
    const app = this.createDialogApp(
      ExchangeConfirm,
      this.dialogAppPluginProps<Partial<ExchangeConfirmProps>>(() => app, el, {
        ...props,
        onConfirm: async () => {
          await props.onConfirm?.()
        },
      }),
    )
    this.mountApp(app, el)
  }

  /**
   * 注册一个登录路由，用于在需要登录的页面跳转到登录页
   * @param toLogin
   */
  static registerLoginRouter(toLogin: () => void) {
    this.gotoLogin = toLogin
  }

  /**
   * 注册一个充值路由，用于在需要充值的页面跳转到充值页
   * @param gotoRecharge
   */
  static registerRechargeRouter(gotoRecharge: () => void) {
    this.gotoRecharge = gotoRecharge
  }

  /**
   * 注册一个安全设置路由，用于在需要安全设置的页面跳转到安全设置页
   * @param gotoSecuritySettings
   */
  static registerSecuritySettingsRouter(gotoSecuritySettings: () => void) {
    this.gotoSecuritySettings = gotoSecuritySettings
  }

  static install(app: App<Element>) {
    this.app = app
  }

  /**
   * 路由
   * @param router
   */
  static useRouter(router: Router) {
    this.router = router
  }

  /**
   * Capture DOM to canvas
   * @param element DOM Element (ref.value)
   * @param options html2canvas options
   * @returns Promise<HTMLCanvasElement>
   */
  static captureDomToCanvas = async (element: HTMLElement, options: Partial<Options> = {}) => {
    if (!element) throw new Error('captureDomToCanvas: element is null')

    const defaultOptions: Partial<Options> = {
      scale: 4,
      useCORS: true,
      allowTaint: false,
      logging: false,
      backgroundColor: null,
      imageTimeout: 15000,
    }

    const finalOptions: Partial<Options> = { ...defaultOptions, ...options }

    // html2canvas 要求 Options 类型，若你没有填 windowX 等字段，可以断言（常见做法）
    return await html2canvas(element, finalOptions as Options)
  }

  /**
   *
   * @param props
   */
  static showSubscriptionFinancial(props?: Partial<SubscriptionFinancialProps>) {
    const el: HTMLDivElement | null = document.createElement('div')
    const app = this.createDialogApp(
      SubscriptionFinancial,
      this.dialogAppPluginProps<Partial<SubscriptionFinancialProps>>(() => app, el, props || {}),
    )
    this.mountApp(app, el)
  }

  static showFinancePositionDetail(props: Partial<FEFinancePositionDetailProps>) {
    const el: HTMLDivElement | null = document.createElement('div')
    const app = this.createDialogApp(
      FinancePositionDetail,
      this.dialogAppPluginProps<Partial<FEFinancePositionDetailProps>>(() => app, el, { ...props, mobileIsDialog: true }),
    )
    this.mountApp(app, el)
  }
}
